/**
 * Survey/campaign flow graph. A flow is a DAG (cycles permitted only via
 * explicit re-ask edges to bound infinite loops). Every node has bilingual
 * content so a single graph runs in en or es per-contact.
 */

export type NodeId = string;
export type Language = "en" | "es";
export type LocalizedText = Record<Language, string>;

export type FlowNode =
  | MessageNode
  | QuestionNode
  | BranchNode
  | TagNode
  | UnsubscribeNode
  | AgentEscalationNode
  | CompletionNode;

interface BaseNode {
  id: NodeId;
  kind: FlowNode["kind"];
  label: string;
  /** Optional position for the visual editor. */
  x?: number;
  y?: number;
}

export interface MessageNode extends BaseNode {
  kind: "message";
  body: LocalizedText;
  next?: NodeId;
}

export interface QuestionNode extends BaseNode {
  kind: "question";
  prompt: LocalizedText;
  /** Allowed answer choices (normalized). Empty = free text. */
  choices: { id: string; label: LocalizedText; aliases?: string[] }[];
  next?: NodeId;
  /** Where to go if response can't be parsed against choices. */
  fallback?: NodeId;
  /** Stores the answer under this key on the contact's response record. */
  answerKey: string;
}

export interface BranchNode extends BaseNode {
  kind: "branch";
  /** Evaluated in order; first match wins. */
  rules: { when: { answerKey: string; equals?: string; in?: string[] }; goto: NodeId }[];
  default: NodeId;
}

export interface TagNode extends BaseNode {
  kind: "tag";
  add?: string[];
  remove?: string[];
  next?: NodeId;
}

export interface UnsubscribeNode extends BaseNode {
  kind: "unsubscribe";
  reason: string;
}

export interface AgentEscalationNode extends BaseNode {
  kind: "agent_escalation";
  queue: string;
  note: LocalizedText;
  next?: NodeId;
}

export interface CompletionNode extends BaseNode {
  kind: "completion";
  summary?: LocalizedText;
}

export interface FlowGraph {
  id: string;
  name: string;
  entry: NodeId;
  nodes: Record<NodeId, FlowNode>;
  /** Locked once published; bump version for any change. */
  version: number;
  publishedAt?: string;
}

export function validateFlow(g: FlowGraph): string[] {
  const errors: string[] = [];
  if (!g.nodes[g.entry]) errors.push(`Entry node ${g.entry} missing`);
  for (const node of Object.values(g.nodes)) {
    const refs: (NodeId | undefined)[] = [];
    switch (node.kind) {
      case "message":
      case "tag":
      case "agent_escalation":
        refs.push(node.next);
        break;
      case "question":
        refs.push(node.next, node.fallback);
        break;
      case "branch":
        refs.push(node.default, ...node.rules.map((r) => r.goto));
        break;
    }
    for (const r of refs) {
      if (r && !g.nodes[r]) {
        errors.push(`Node ${node.id} references missing node ${r}`);
      }
    }
  }
  return errors;
}
